import type { LegalDocId } from './legal/legalDocuments'
import { stripLocalePrefix } from './i18n/localeUrl'

/** Web app screens — matches frontend `Page` ids. Catalog uses page `buy`. */
export type AppRoutePage =
  | 'home'
  | 'buy'
  | 'sell'
  | 'examples'
  | 'myListings'
  | 'myOffers'
  | 'chats'
  | 'admin'
  | 'favorites'
  | 'cart'
  | 'notifications'
  | 'reports'
  | 'account'
  | 'guideArticles'

export type AppSellEntry = 'wizard' | 'super-fast'

export type AppOfferRole = 'seller' | 'buyer'

export interface AppCatalogPreset {
  q?: string
  condition?: string
  warranty?: '' | 'yes' | 'no'
  usage?: string
  focusSearch?: boolean
}

export interface AppRouteState {
  page: AppRoutePage
  listingId?: number | null
  offerId?: number | null
  offerRole?: AppOfferRole
  legalDoc?: LegalDocId | null
  sellEntry?: AppSellEntry
  catalogPreset?: AppCatalogPreset | null
  accountFocusVerify?: boolean
}

const LEGAL_DOC_IDS: LegalDocId[] = [
  'privacy',
  'terms',
  'buyer_protection',
  'eula',
  'acceptable_use',
  'support',
  'licenses',
  'account_data',
]

function isLegalDocId(value: string): value is LegalDocId {
  return (LEGAL_DOC_IDS as string[]).includes(value)
}

function parsePositiveInt(raw: string | null | undefined): number | null {
  if (!raw) return null
  const id = parseInt(raw, 10)
  return Number.isFinite(id) && id > 0 ? id : null
}

export function parseCatalogPreset(search: string): AppCatalogPreset | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const q = params.get('q')?.trim()
  const condition = params.get('condition')?.trim()
  const warranty = params.get('warranty')?.trim()
  const usage = params.get('usage')?.trim()
  const focusSearch = params.get('focus') === 'search'
  const preset: AppCatalogPreset = {}
  if (q) preset.q = q
  if (condition) preset.condition = condition
  if (warranty === 'yes' || warranty === 'no' || warranty === '') preset.warranty = warranty
  if (usage) preset.usage = usage
  if (focusSearch) preset.focusSearch = true
  return Object.keys(preset).length > 0 ? preset : null
}

function catalogPresetSearch(preset: AppCatalogPreset | null | undefined): string {
  if (!preset) return ''
  const params = new URLSearchParams()
  if (preset.q?.trim()) params.set('q', preset.q.trim())
  if (preset.condition?.trim()) params.set('condition', preset.condition.trim())
  if (preset.warranty === 'yes' || preset.warranty === 'no') params.set('warranty', preset.warranty)
  if (preset.usage?.trim()) params.set('usage', preset.usage.trim())
  if (preset.focusSearch) params.set('focus', 'search')
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/** Build path + search (without locale prefix). */
export function buildAppPath(state: AppRouteState): string {
  const listingId = state.listingId ?? null
  if (listingId != null) {
    return `/listing/${listingId}`
  }

  const offerId = state.offerId ?? null
  if (offerId != null) {
    const role = state.offerRole === 'buyer' ? 'buyer' : 'seller'
    const qs = role === 'seller' ? '' : `?role=${role}`
    return `/offer/${offerId}${qs}`
  }

  const legalDoc = state.legalDoc ?? null
  if (legalDoc) {
    return `/legal/${legalDoc}`
  }

  switch (state.page) {
    case 'home':
      return '/'
    case 'buy':
      return `/catalog${catalogPresetSearch(state.catalogPreset)}`
    case 'sell':
      return state.sellEntry === 'super-fast' ? '/sell/fast' : '/sell'
    case 'favorites':
      return '/favorites'
    case 'account': {
      const qs = state.accountFocusVerify ? '?verify=seller' : ''
      return `/account${qs}`
    }
    case 'myListings':
      return '/my-listings'
    case 'myOffers':
      return '/my-offers'
    case 'chats':
      return '/chats'
    case 'notifications':
      return '/notifications'
    case 'reports':
      return '/reports'
    case 'cart':
      return '/cart'
    case 'admin':
      return '/admin'
    case 'examples':
      return '/examples'
    case 'guideArticles':
      return '/guide'
    default:
      return '/'
  }
}

export function parseAppPath(pathname: string, search = ''): AppRouteState {
  const pathOnly = pathname.split('?')[0]
  const effectiveSearch =
    search || (pathname.includes('?') ? pathname.slice(pathname.indexOf('?')) : '')
  const rest = stripLocalePrefix(pathOnly)

  const normalized = rest.endsWith('/') && rest.length > 1 ? rest.slice(0, -1) : rest
  const params = new URLSearchParams(
    effectiveSearch.startsWith('?') ? effectiveSearch.slice(1) : effectiveSearch,
  )

  const legacyListingId = parsePositiveInt(params.get('listing'))
  if (legacyListingId != null && (normalized === '/' || normalized === '')) {
    return { page: 'buy', listingId: legacyListingId }
  }

  const segments = normalized.split('/').filter(Boolean)
  if (segments.length === 0) {
    return { page: 'home' }
  }

  const [head, second] = segments

  if (head === 'listing') {
    const listingId = parsePositiveInt(second)
    if (listingId != null) {
      return { page: 'buy', listingId }
    }
  }

  if (head === 'offer') {
    const offerId = parsePositiveInt(second)
    if (offerId != null) {
      const roleRaw = params.get('role')
      const offerRole: AppOfferRole = roleRaw === 'buyer' ? 'buyer' : 'seller'
      return { page: offerRole === 'buyer' ? 'myOffers' : 'myListings', offerId, offerRole }
    }
  }

  if (head === 'legal' && second && isLegalDocId(second)) {
    return { page: 'home', legalDoc: second }
  }

  if (head === 'catalog' || head === 'buy') {
    return { page: 'buy', catalogPreset: parseCatalogPreset(effectiveSearch) }
  }

  if (head === 'sell') {
    return { page: 'sell', sellEntry: second === 'fast' ? 'super-fast' : 'wizard' }
  }

  const pageMap: Record<string, AppRoutePage> = {
    favorites: 'favorites',
    account: 'account',
    'my-listings': 'myListings',
    'my-offers': 'myOffers',
    chats: 'chats',
    notifications: 'notifications',
    reports: 'reports',
    cart: 'cart',
    admin: 'admin',
    examples: 'examples',
    guide: 'guideArticles',
  }

  const mapped = pageMap[head]
  if (mapped === 'account') {
    return {
      page: 'account',
      accountFocusVerify: params.get('verify') === 'seller',
    }
  }
  if (mapped) {
    return { page: mapped }
  }

  return { page: 'home' }
}

export function appPathsEqual(a: string, b: string): boolean {
  return a === b
}
